import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Locale } from '@et/schema';
import { SegmentedControl } from '../controls/SegmentedControl.js';
import { Switch } from '../controls/Switch.js';
import { TimelineCanvas } from './TimelineCanvas.js';
import { TimelineList } from './TimelineList.js';
import { useTimelineViewport } from './useTimelineViewport.js';
import type { AxisMode } from './scale.js';
import type { TimelineEra, TimelinePin } from './types.js';

export interface TimelineProps {
  readonly pins: readonly TimelinePin[];
  readonly antiquity: readonly [number, number];
  readonly discovery: readonly [number, number];
  readonly width?: number;
  readonly rows?: number;
  readonly locale?: Locale;
  readonly selectedId?: string | null;
  readonly onSelect?: (id: string | null) => void;
  readonly initialMode?: AxisMode;
  readonly eras?: readonly TimelineEra[];
}

type View = 'timeline' | 'list';

/**
 * A largura do desenho vem do contentor, e nao de uma constante.
 *
 * Com `width` fixo em 1200 e o SVG a `width: 100%`, um contentor de 700px
 * encolhia o desenho todo para 58% — a regua saia com onze pixeis de altura e
 * os anos ilegiveis, dentro de um painel com 250px de espaco vazio por baixo.
 * Medir o contentor faz o desenho sair a escala 1:1 e ocupar o que tem.
 *
 * Em jsdom `getBoundingClientRect` devolve zero, e ai vale o valor recebido
 * por propriedade — que e o que os testes passam.
 */
function useMeasuredWidth(fallback: number) {
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(0);
  useLayoutEffect(() => {
    const node = ref.current;
    if (node === null || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(([entry]) => {
      const w = entry?.contentRect.width ?? 0;
      if (w > 0) setMeasured(Math.round(w));
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (node !== null && measured === 0) {
      const w = Math.round(node.getBoundingClientRect().width);
      if (w > 0) setMeasured(w);
    }
  }, [measured]);
  return { ref, width: measured > 0 ? measured : fallback };
}

/**
 * O contentor: grafico e lista sobre **o mesmo conjunto**, ligados por
 * `aria-controls`, mais os dois toggles que o design pede — eixo dividido ou
 * escala continua, e datacoes alternativas.
 */
export function Timeline({
  pins, antiquity, discovery, width = 1200, rows = 3, locale = 'en',
  selectedId = null, onSelect, initialMode = 'split', eras,
}: TimelineProps) {
  const { t } = useTranslation();
  const listId = useId();
  const [view, setView] = useState<View>('timeline');
  const [alternatives, setAlternatives] = useState(false);
  /*
    A dica e para a primeira visita, e ruido para todas as outras.

    Fica ate ao primeiro arrasto ou zoom, e a partir dai lembra-se — em
    `localStorage`, que pode nao existir (modo privado, jsdom); nesse caso
    volta a aparecer, que e o lado certo para errar.
  */
  const [interacted, setInteracted] = useState<boolean>(() => {
    try { return window.localStorage.getItem('et:timeline-hint') === 'seen'; } catch { return false; }
  });
  const markInteracted = () => {
    if (interacted) return;
    setInteracted(true);
    try { window.localStorage.setItem('et:timeline-hint', 'seen'); } catch { /* sem armazenamento */ }
  };
  // A goteira dos nomes das faixas vive fora do dominio da escala, por isso
  // desconta-se aqui: o que a escala recebe e o espaco que sobra para o tempo.
  const measured = useMeasuredWidth(width);
  const drawWidth = Math.max(320, measured.width - 92);
  const vp = useTimelineViewport({ antiquity, discovery, width: drawWidth, initialMode });

  /*
    Escolher na lista traz o grafico ate a peca.

    A lista promete ser um indice; se a peca escolhida estiver fora do zoom
    actual, a marca acende-se fora do ecra e a promessa falha. So se move
    quando e preciso — uma peca ja visivel nao mexe a janela, para nao
    puxar o grafico debaixo do cursor a cada clique.
  */
  /*
    "Esta visivel?" decide-se pelo dominio da janela, nao por `toX`: a escala
    fixa na borda os anos fora do dominio, e por isso uma peca de 1213 aC com
    a janela em 650-800 dC dava x = 0 e parecia estar no ecra.
  */
  const { centerOn, viewport, mode } = vp;
  useEffect(() => {
    if (selectedId === null) return;
    const pin = pins.find((p) => p.id === selectedId);
    if (pin === undefined) return;
    const year = pin.event[0];
    // No eixo partido a faixa das descobertas esta sempre inteira no ecra.
    if (mode === 'split' && year >= discovery[0]) return;
    const [lo, hi] = viewport.domain;
    if (year >= lo && year <= hi) return;
    centerOn(year);
  // So quando a escolha muda: mover a janela por causa de um zoom seria
  // desfazer o que a pessoa acabou de fazer.
  }, [selectedId]);

  return (
    <div className="et-timeline">
      <div className="et-timeline__controls">
        <SegmentedControl<View>
          label={t('timeline.view')}
          value={view}
          onValueChange={setView}
          options={[
            { value: 'timeline', label: t('timeline.graph') },
            { value: 'list', label: t('timeline.list') },
          ]}
        />
        {/* §6 — o eixo dividido e a resposta por omissao, nao a unica vista. */}
        <SegmentedControl<AxisMode>
          label={t('timeline.axis')}
          value={vp.mode}
          onValueChange={vp.setMode}
          options={[
            { value: 'split', label: t('timeline.axisSplit') },
            { value: 'continuous', label: t('timeline.axisContinuous') },
          ]}
        />
        <Switch
          checked={alternatives}
          onCheckedChange={setAlternatives}
          label={t('timeline.alternativeDatings')}
        />
      </div>

      {view === 'timeline' ? (
        <>
          <div className="et-timeline__scroll" ref={measured.ref}>
            <TimelineCanvas
              pins={pins}
              scale={vp.scale}
              width={drawWidth}
              rows={rows}
              locale={locale}
              selectedId={selectedId}
              showAlternativeDatings={alternatives}
              {...(eras === undefined ? {} : { eras })}
              onSelect={onSelect ?? (() => undefined)}
              onPan={(dx) => { markInteracted(); vp.pan(dx, drawWidth); }}
              onZoom={(factor, x) => { markInteracted(); vp.zoom(factor, x, drawWidth); }}
              listId={listId}
            />
          </div>
          {/*
            A lista existe sempre na arvore, ligada por `aria-controls`.
            Escondida visualmente, **nao** com `hidden`: o atributo tira-a da
            arvore de acessibilidade, e ai o `aria-controls` apontaria para
            nada. Assim quem usa leitor de ecra tem o equivalente sem ter de
            encontrar o toggle primeiro.
          */}
          <div className="et-visually-hidden">
            <TimelineList id={listId} pins={pins} locale={locale} selectedId={selectedId} />
          </div>
        </>
      ) : (
        <TimelineList
          id={listId} pins={pins} locale={locale}
          selectedId={selectedId} onSelect={onSelect ?? (() => undefined)}
        />
      )}

      {!interacted && <p className="et-timeline__hint">{t('timeline.hint')}</p>}
    </div>
  );
}
